import React from 'react';
import { NavigateFunction } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Chip, Dropdown, DropdownOption, Icon, formatNumber } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, PageHeader, PaginationWrapper, PopoverFilterEntry, TableColumn } from '~/components';
import { RelativeTime } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { LoanRepository } from '~/repositories';

const Description = styled.div`
  display: grid;
  grid-gap: 2px;
`;

const Note = styled.div`
  line-height: 16px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

const getDisbursementStatusChip = (status: string | undefined) => {
  if (!status) return null;

  const statusLower = status.toLowerCase();

  if (statusLower === 'completed') {
    return (
      <Chip icon={<Icon.CircleCheck />} variant="subtle" type="success">
        Completed
      </Chip>
    );
  }
  if (statusLower === 'cancelled' || statusLower === 'canceled') {
    return (
      <Chip icon={<Icon.CircleCross />} variant="subtle" type="danger">
        Cancelled
      </Chip>
    );
  }
  if (statusLower === 'hold') {
    return (
      <Chip icon={<Icon.Warning />} variant="subtle" type="warning">
        Hold
      </Chip>
    );
  }

  return (
    <Chip variant="subtle" type="default">
      {status}
    </Chip>
  );
};

export const handleFetchDisbursements = async (params: any, navigate: NavigateFunction, addLoanLink?: boolean) => {
  return LoanRepository.getAllDisbursements(params).then((response) => {
    const entries: any = [];

    if (response.disbursements.length) {
      response.disbursements.map((entry: any) => {
        const options: DropdownOption[] = [];

        if (addLoanLink) {
          options.push({
            label: 'Loan',
            onClick: () => navigate(`${ROUTE.LOANS}/edit/${entry.loanId}`),
          });
        }

        entries.push({
          id: entry.id,
          description: [entry?.amount ?? 0, entry.description],
          status: entry.status,
          created: entry.createdAt,
          action: (
            <Dropdown
              isInside
              positionRight
              options={[
                ...options,
                {
                  label: 'Edit',
                  onClick: () => navigate(`${ROUTE.LOANS}/disbursements/edit/${entry.id}`),
                },
              ]}
              variant="dots"
            />
          ),
        });
      });
    }
    return { entries, hasMore: Boolean(response?.hasMore) };
  });
};

export const disbursementColumns: TableColumn[] = [
  {
    Header: 'Description',
    accessor: 'description',
    width: 'minmax(300px, auto)',
    Cell: (current) => (
      <Description>
        {formatNumber(current.value[0])}
        {current.value[1] && <Note>{current.value[1]}</Note>}
      </Description>
    ),
  },
  {
    Header: 'Status',
    width: 'minmax(min-content, 100px)',
    Cell: (current) => getDisbursementStatusChip(current.value),
    accessor: 'status',
  },
  {
    Header: 'Created',
    accessor: 'created',
    sortType: 'datetime',
    width: 'min-content',
    Cell: (current) => <RelativeTime timestamp={current.value} />,
  },
  {
    Header: '',
    width: 'min-content',
    accessor: 'action',
  },
];

export const disbursementFilter: PopoverFilterEntry[] = [
  {
    id: 'created',
    label: 'Created',
    type: 'date',
  },
];

export const PageLoansDisbursementOverview: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <PageHeader text="Disbursements" border={false}>
        <Button onClick={() => navigate(ROUTE.LOANS)} icon={<Icon.HandCoins />} size="small" variant="secondary">
          Loans
        </Button>
        <Button onClick={() => navigate(`${ROUTE.LOANS}/disbursements/create`)} icon={<Icon.Plus />} size="small">
          Create Disbursement
        </Button>
      </PageHeader>

      <PaginationWrapper
        tableId="disbursements"
        fetch={(o) => handleFetchDisbursements(o, navigate, true)}
        columns={disbursementColumns}
        filter={disbursementFilter}
        empty={
          <EmptyState headline="No disbursements found" text="Create your first disbursement to get started.">
            <Button onClick={() => navigate(`${ROUTE.LOANS}/disbursements/create`)} size="small" icon={<Icon.Plus />}>
              Create Disbursement
            </Button>
          </EmptyState>
        }
        rowClick={(row) => {
          navigate(`${ROUTE.LOANS}/disbursements/edit/${row.original.id}`);
        }}
      />
    </>
  );
};
